import { useState, useRef, useEffect } from "react"
import { MagnifyingGlass } from "phosphor-react"
import styles from "./FaqResultView.module.css"
import { translations } from "../../translations"

type FaqData = {
  question: string
  answer: string
}

type Props = {
  selectedFaq: FaqData
  faqsData: FaqData[]
  onSelectFaq: (faq: FaqData) => void
}

export default function FaqResultView({ selectedFaq, faqsData, onSelectFaq }: Props) {
  const [resultsSearchValue, setResultsSearchValue] = useState("")
  const [isResultsSearchOpen, setIsResultsSearchOpen] = useState(false)
  const resultsInputRef = useRef<HTMLInputElement>(null)
  const resultsWrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (resultsWrapperRef.current && !resultsWrapperRef.current.contains(e.target as Node)) {
        setIsResultsSearchOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleResultsItemClick = (faq: FaqData) => {
    setResultsSearchValue("")
    setIsResultsSearchOpen(false)
    onSelectFaq(faq)
  }

  const filteredResultsFaqs = faqsData.filter(faq => 
    faq.question.toLowerCase().includes(resultsSearchValue.toLowerCase())
  )

  const renderAnswer = (answer: string) => {
    return answer.split('\n').map((line, index) => (
      <span key={index}>
        {line}
        {index < answer.split('\n').length - 1 && <br />}
      </span>
    ))
  }

  return (
    <>
      <div className={styles.searchHeader}>
        <h1 className={styles.logo}>
          <span style={{ color: '#4285F4' }}>F</span>
          <span style={{ color: '#DB4437' }}>r</span>
          <span style={{ color: '#F4B400' }}>e</span>
          <span style={{ color: '#4285F4' }}>q</span>
          <span style={{ color: '#0F9D58' }}>u</span>
          <span style={{ color: '#DB4437' }}>e</span>
          <span style={{ color: '#4285F4' }}>n</span>
          <span style={{ color: '#F4B400' }}>t</span>
          <span style={{ color: '#0F9D58' }}>l</span>
          <span style={{ color: '#DB4437' }}>y</span>
        </h1>
        
        <div className={styles.resultsSearchWrapper} ref={resultsWrapperRef}>
          <div 
            className={`${styles.searchBox} ${isResultsSearchOpen ? styles.searchBoxOpen : ''}`}
            onClick={() => {
              setIsResultsSearchOpen(true)
              resultsInputRef.current?.focus()
            }}
          >
            <MagnifyingGlass size={20} color="#666" />
            <input
              ref={resultsInputRef}
              className={styles.searchInput}
              value={resultsSearchValue}
              onChange={(e) => setResultsSearchValue(e.target.value)}
              onFocus={() => setIsResultsSearchOpen(true)}
              placeholder={selectedFaq.question}
            />
          </div>

          {isResultsSearchOpen && (
            <div className={styles.resultsDropdown}>
              {filteredResultsFaqs.length > 0 ? (
                filteredResultsFaqs.map((faq, index) => (
                  <button
                    key={index}
                    className={styles.dropdownItem}
                    onClick={() => handleResultsItemClick(faq)}
                  >
                    {faq.question}
                  </button>
                ))
              ) : resultsSearchValue ? (
                <div className={styles.noResults}>{translations('faq.no_results')}</div>
              ) : (
                faqsData.map((faq, index) => (
                  <button
                    key={index}
                    className={styles.dropdownItem}
                    onClick={() => handleResultsItemClick(faq)}
                  >
                    {faq.question}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      <div className={styles.resultsContainer}>
        <div className={styles.resultStats}>
          {translations('faq.result_stats')}
        </div>

        <div className={styles.resultItem}>
          <div className={styles.resultUrl}>
            <div className={styles.urlInfo}>
              <div className={styles.urlBreadcrumb}>{translations('faq.breadcrumb')}</div>
              <div className={styles.resultTitle}>{selectedFaq.question}</div>
            </div>
          </div>
          
          <div className={styles.resultDescription}>
            {renderAnswer(selectedFaq.answer)}
          </div>
        </div>
      </div>
    </>
  )
}